import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import MainContainer from "../../components/MainContainer";
import SeoHead from "../../components/Seo/SeoHead.js";
import SeoTemplateShelf from "../../components/SeoTemplateShelf/SeoTemplateShelf.js";
import {
  buildArticleSchema,
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildPageTitle,
  buildWebApplicationSchema,
  buildWebsiteSchema,
} from "../../seo/siteSeo.js";
import { selectLoggedIn } from "../../store/slices/auth/slice.js";
import { setSignUpModalOpen } from "../../store/slices/common/slice.js";
import {
  ActionRow,
  Answer,
  ArticleExcerpt,
  ArticleMeta,
  CardLabel,
  CardText,
  ContentGrid,
  Eyebrow,
  FaqItem,
  FaqSection,
  Hero,
  HeroCard,
  HeroCopy,
  Intro,
  KeywordList,
  LinkCloud,
  LinkPill,
  MainContent,
  MetaPill,
  MetaRow,
  Page,
  Paragraph,
  PrimaryAction,
  PrimaryActionButton,
  PromoCopy,
  PromoSection,
  PromoText,
  PromoTitle,
  Question,
  SectionCard,
  SectionTitle,
  SecondaryAction,
  Sidebar,
  StepBody,
  StepCopy,
  StepHeading,
  StepItem,
  StepList,
  StepNumber,
  Title,
  BulletList,
} from "./SeoArticlePage.styles";

const SeoArticlePage = ({ article }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loggedIn = useSelector(selectLoggedIn);
  const primaryActionLabel = loggedIn ? "Open Fapify" : "Try Fapify Free";
  const articleSteps = [
    {
      title: "Browse a template",
      text: "Start from a public setup that already matches the scene, style, or idea you want.",
    },
    {
      title: "Refine the frame",
      text: "Use editing and compatible tools to tighten the source image before generation.",
    },
    {
      title: "Generate the final result",
      text: "Create the still or short clip, then reuse the setup if you want more variations.",
    },
  ];

  const handleOpenProduct = () => {
    if (loggedIn) {
      navigate("/");
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
      return;
    }

    dispatch(setSignUpModalOpen(true));
  };

  const jsonLd = [
    buildWebsiteSchema(),
    buildWebApplicationSchema({
      name: `${article.h1} | Fapify`,
      description: article.description,
      url: `https://www.fapify.com${article.path}`,
    }),
    buildBreadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Guides", path: "/blog" },
      { name: article.h1, path: article.path },
    ]),
    buildArticleSchema({
      headline: article.h1,
      description: article.description,
      path: article.path,
      datePublished: article.datePublished,
      dateModified: article.dateModified,
      keywords: article.keywords,
    }),
    buildFaqSchema(article.faq),
  ];

  return (
    <MainContainer hideTabs={false} fullWidth>
      <SeoHead
        title={buildPageTitle(article.title)}
        description={article.description}
        path={article.path}
        keywords={article.keywords}
        ogType="article"
        jsonLd={jsonLd}
      />

      <Page>
        <Hero>
          <HeroCopy>
            <Eyebrow>Fapify Guide</Eyebrow>
            <Title>{article.h1}</Title>
            <Intro>{article.intro}</Intro>
            <MetaRow>
              <MetaPill>{article.readTime}</MetaPill>
              <MetaPill>Published {article.datePublished}</MetaPill>
              <MetaPill>Updated {article.dateModified}</MetaPill>
            </MetaRow>
            <ActionRow>
              <PrimaryActionButton type="button" onClick={handleOpenProduct}>
                {primaryActionLabel}
              </PrimaryActionButton>
              <PrimaryAction to="/">Browse Templates</PrimaryAction>
              <SecondaryAction to="/blog">View All Guides</SecondaryAction>
            </ActionRow>
          </HeroCopy>

          <HeroCard>
            <div>
              <CardLabel>Try Fapify Free</CardLabel>
              <CardText>{article.excerpt}</CardText>
            </div>

            <StepList>
              {articleSteps.map((step, index) => (
                <StepItem key={step.title}>
                  <StepNumber>{index + 1}</StepNumber>
                  <StepCopy>
                    <StepHeading>{step.title}</StepHeading>
                    <StepBody>{step.text}</StepBody>
                  </StepCopy>
                </StepItem>
              ))}
            </StepList>

            <ActionRow>
              <PrimaryActionButton type="button" onClick={handleOpenProduct}>
                {primaryActionLabel}
              </PrimaryActionButton>
              <SecondaryAction to="/subscription">View Pricing</SecondaryAction>
            </ActionRow>

            <div>
              <CardLabel>Popular Topics</CardLabel>
              <KeywordList>
                {article.keywords.map((keyword) => (
                  <li key={keyword}>{keyword}</li>
                ))}
              </KeywordList>
            </div>
          </HeroCard>
        </Hero>

        <SeoTemplateShelf
          title="Try These Templates Next"
          description="Start with one of these templates and put the guide into action right away."
          preferredKeyword={article.keywords?.[2] || article.keywords?.[1]}
        />

        <ContentGrid>
          <MainContent>
            {article.sections.map((section) => (
              <SectionCard key={section.title}>
                <SectionTitle>{section.title}</SectionTitle>
                {section.paragraphs?.map((paragraph) => (
                  <Paragraph key={paragraph}>{paragraph}</Paragraph>
                ))}
                {section.bullets?.length ? (
                  <BulletList>
                    {section.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </BulletList>
                ) : null}
              </SectionCard>
            ))}

            <PromoSection>
              <PromoCopy>
                <PromoTitle>Ready to try Fapify?</PromoTitle>
                <PromoText>
                  Open the app, browse a live template, and move from guide to
                  generation without losing the workflow you just learned.
                </PromoText>
              </PromoCopy>

              <ActionRow>
                <PrimaryActionButton type="button" onClick={handleOpenProduct}>
                  {primaryActionLabel}
                </PrimaryActionButton>
                <SecondaryAction to="/">Browse Templates</SecondaryAction>
              </ActionRow>
            </PromoSection>

            <FaqSection>
              <SectionTitle>Frequently Asked Questions</SectionTitle>
              {article.faq.map((item) => (
                <FaqItem key={item.question}>
                  <Question>{item.question}</Question>
                  <Answer>{item.answer}</Answer>
                </FaqItem>
              ))}
            </FaqSection>
          </MainContent>

          <Sidebar>
            <SectionCard>
              <CardLabel>Related Pages</CardLabel>
              <LinkCloud>
                {article.relatedLinks.map((link) => (
                  <LinkPill key={link.href} to={link.href}>
                    {link.label}
                  </LinkPill>
                ))}
              </LinkCloud>
            </SectionCard>

            <SectionCard>
              <CardLabel>Article Snapshot</CardLabel>
              <ArticleMeta>{article.readTime}</ArticleMeta>
              <ArticleExcerpt>{article.description}</ArticleExcerpt>
            </SectionCard>
          </Sidebar>
        </ContentGrid>
      </Page>
    </MainContainer>
  );
};

export default SeoArticlePage;
